import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { IBasicStyledProps } from '@notino/react-styleguide-v20';

import { GridDivider } from '@sharedComponents/Separators';
import { GeneralTextService, ServiceItem } from '@sharedComponents/Service';
import { ReactFCV } from '@types';
import { DIVIDER_COLOR, DIVIDER_HEIGHT } from '@utils/constants';
import { CypressTestNamespace } from '@utils/test';

import { IToggleInsuranceAction } from '../../../context';
import { IInsurance } from '../../../model';
import { messages } from '../messages';
import { getInsuranceModel } from '../utils';

import { InsuranceDescription } from './InsuranceDescription';

export interface IInsuranceServiceProps extends IBasicStyledProps {
  insuranceEnabled: boolean;
  insurance?: IInsurance | null;
  insurancePrice: number;
  toggleInsurance: (data: IToggleInsuranceAction) => void;
  isDualPriceLayout: boolean;
  showServiceInfoModal: boolean;
}

export const InsuranceService: ReactFCV<IInsuranceServiceProps> = React.memo(
  ({ insurance, insurancePrice, toggleInsurance, isDualPriceLayout, insuranceEnabled, showServiceInfoModal }) => {
    const { formatMessage } = useIntl();
    const model = getInsuranceModel(insurancePrice, insurance);

    const onChangeInsurance = useCallback(() => {
      toggleInsurance({
        service: insurance,
        active: !insurance,
      });
    }, [toggleInsurance, insurance]);

    const infoText = formatMessage({ ...messages.insuranceDescription });

    if (!insuranceEnabled) {
      return null;
    }

    return (
      <>
        <GridDivider color={DIVIDER_COLOR} height={DIVIDER_HEIGHT} />
        <ServiceItem
          data-testid="insurance-service"
          price={model.price}
          isDualPriceLayout={isDualPriceLayout}
          title={
            <GeneralTextService
              title={formatMessage(messages.insurance)}
              infoMarkText={formatMessage(messages.serviceLegalInfo)}
            />
          }
          infoText={
            showServiceInfoModal ? (
              <InsuranceDescription infoText={infoText} />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: infoText }} />
            )
          }
          serviceActive={model.checked}
          serviceDisabled={!insuranceEnabled}
          onClick={onChangeInsurance}
          freePriceText={<FormattedMessage {...messages.forFreePriceText} />}
          dataTestId={CypressTestNamespace.ECartPageServices.INSURANCE_SERVICE}
        />
      </>
    );
  },
);
